import React, { useState, Children } from 'react';
import { SearchIcon, ZoomInIcon, ZoomOutIcon, RotateCcwIcon, ChevronDownIcon, ChevronUpIcon, LayoutGridIcon, GitBranchIcon, MapPinIcon, MailIcon, PhoneIcon } from 'lucide-react';
interface Person {
  id: string;
  name: string;
  title: string;
  dept: string;
  color: string;
  avatar: string;
  email: string;
  phone: string;
  location: string;
  children: Person[];
}
const orgTree: Person = {
  id: 'jw',
  name: 'Jennifer Walsh',
  title: 'CEO & Founder',
  dept: 'Executive',
  color: '#0A1628',
  avatar: 'JW',
  email: 'jennifer@swifthl.com',
  phone: '(602) 555-0101',
  location: 'Phoenix, AZ',
  children: [{
    id: 'rc',
    name: 'Robert Chen',
    title: 'Chief Operating Officer',
    dept: 'Executive',
    color: '#1A4FD8',
    avatar: 'RC',
    email: 'robert@swifthl.com',
    phone: '(602) 555-0102',
    location: 'Phoenix, AZ',
    children: [{
      id: 'cm',
      name: 'Carlos Mendez',
      title: 'Branch Manager',
      dept: 'Sales',
      color: '#1A4FD8',
      avatar: 'CM',
      email: 'carlos@swifthl.com',
      phone: '(602) 555-0203',
      location: 'Phoenix, AZ',
      children: [{
        id: 'sk',
        name: 'Sarah Kim',
        title: 'Sr. Loan Originator',
        dept: 'Sales',
        color: '#FF6B2B',
        avatar: 'SK',
        email: 'sarah@swifthl.com',
        phone: '(602) 555-0201',
        location: 'Phoenix, AZ',
        children: []
      }, {
        id: 'pr',
        name: 'Priya Rajan',
        title: 'Loan Originator',
        dept: 'Sales',
        color: '#FF6B2B',
        avatar: 'PR',
        email: 'priya@swifthl.com',
        phone: '(512) 555-0202',
        location: 'Austin, TX',
        children: []
      }, {
        id: 'ac',
        name: 'Alex Chen',
        title: 'Loan Originator',
        dept: 'Sales',
        color: '#FF6B2B',
        avatar: 'AC',
        email: 'alex@swifthl.com',
        phone: '(602) 555-0204',
        location: 'Phoenix, AZ',
        children: []
      }]
    }, {
      id: 'mt',
      name: 'Marcus Thompson',
      title: 'Lead Processor',
      dept: 'Operations',
      color: '#22C55E',
      avatar: 'MT',
      email: 'marcus@swifthl.com',
      phone: '(214) 555-0301',
      location: 'Dallas, TX',
      children: [{
        id: 'ms',
        name: 'Maria Santos',
        title: 'Processor',
        dept: 'Operations',
        color: '#22C55E',
        avatar: 'MS',
        email: 'maria@swifthl.com',
        phone: '(214) 555-0303',
        location: 'Dallas, TX',
        children: []
      }, {
        id: 'dl',
        name: 'Derek Liu',
        title: 'Processor',
        dept: 'Operations',
        color: '#22C55E',
        avatar: 'DL',
        email: 'derek@swifthl.com',
        phone: '(888) 555-0302',
        location: 'Remote',
        children: []
      }]
    }, {
      id: 'jp',
      name: 'James Park',
      title: 'Senior Underwriter',
      dept: 'Underwriting',
      color: '#F5A623',
      avatar: 'JP',
      email: 'james@swifthl.com',
      phone: '(888) 555-0501',
      location: 'Remote',
      children: []
    }]
  }, {
    id: 'lm',
    name: 'Lisa Martinez',
    title: 'Training Manager',
    dept: 'HR & Training',
    color: '#EC4899',
    avatar: 'LM',
    email: 'lisa@swifthl.com',
    phone: '(602) 555-0401',
    location: 'Phoenix, AZ',
    children: []
  }, {
    id: 'tw',
    name: 'Tanya Williams',
    title: 'Marketing Specialist',
    dept: 'Marketing',
    color: '#8B5CF6',
    avatar: 'TW',
    email: 'tanya@swifthl.com',
    phone: '(602) 555-0601',
    location: 'Phoenix, AZ',
    children: []
  }, {
    id: 'dk',
    name: 'David Kim',
    title: 'IT Support Lead',
    dept: 'Technology',
    color: '#0EA5E9',
    avatar: 'DK',
    email: 'david@swifthl.com',
    phone: '(602) 555-0701',
    location: 'Phoenix, AZ',
    children: []
  }]
};
const deptColors: Record<string, string> = {
  Executive: '#0A1628',
  Sales: '#FF6B2B',
  Operations: '#22C55E',
  Underwriting: '#F5A623',
  'HR & Training': '#EC4899',
  Marketing: '#8B5CF6',
  Technology: '#0EA5E9'
};
function flattenTree(node: Person): Person[] {
  return [node, ...node.children.flatMap(flattenTree)];
}
const allPeople = flattenTree(orgTree);
// ─── Node Card ────────────────────────────────────────────────────────────────
function NodeCard({
  person,
  isMatch,
  isSelected,
  hasChildren,
  collapsed,
  onToggle,
  onSelect
}: {
  person: Person;
  isMatch: boolean;
  isSelected: boolean;
  hasChildren: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onSelect: (p: Person | null) => void;
}) {
  const deptColor = deptColors[person.dept] || '#1A4FD8';
  return <div onClick={() => onSelect(isSelected ? null : person)} style={{
    width: 164,
    background: '#FFFFFF',
    borderRadius: 14,
    padding: '14px 12px 10px',
    border: isMatch ? '2px solid #1A4FD8' : isSelected ? `2px solid ${person.color}` : '1.5px solid #E4EAF6',
    boxShadow: isMatch ? '0 0 0 4px rgba(26,79,216,0.12), 0 2px 8px rgba(10,22,40,0.08)' : isSelected ? `0 0 0 4px ${person.color}20, 0 4px 16px rgba(10,22,40,0.12)` : '0 2px 8px rgba(10,22,40,0.06)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    flexShrink: 0
  }} onMouseEnter={e => {
    if (!isSelected && !isMatch) {
      ;
      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(10,22,40,0.12)';
      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
    }
  }} onMouseLeave={e => {
    if (!isSelected && !isMatch) {
      ;
      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(10,22,40,0.06)';
      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
    }
  }} data-id="element-163">
      {/* Avatar */}
      <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: 8
    }} data-id="element-164">
        <div style={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${person.color} 0%, ${person.color}99 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
        fontWeight: 900,
        color: 'white',
        transform: 'skewX(-3deg)',
        boxShadow: `0 4px 12px ${person.color}40`,
        border: `2px solid ${person.color}30`
      }} data-id="element-165">
          {person.avatar}
        </div>
      </div>

      {/* Name */}
      <div style={{
      fontSize: 13,
      fontWeight: 900,
      color: '#0D1B3E',
      textAlign: 'center',
      fontStyle: 'italic',
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      marginBottom: 3
    }} data-id="element-166">
        {person.name}
      </div>

      {/* Title */}
      <div style={{
      fontSize: 10,
      color: '#5A6A8A',
      textAlign: 'center',
      lineHeight: 1.3,
      marginBottom: 6,
      fontWeight: 500
    }} data-id="element-167">
        {person.title}
      </div>

      {/* Dept badge */}
      <div style={{
      display: 'flex',
      justifyContent: 'center',
      marginBottom: hasChildren ? 8 : 0
    }} data-id="element-168">
        <span style={{
        fontSize: 9,
        fontWeight: 800,
        padding: '2px 7px',
        borderRadius: 4,
        background: `${deptColor}15`,
        color: deptColor,
        letterSpacing: '0.04em',
        textTransform: 'uppercase'
      }} data-id="element-169">
          {person.dept}
        </span>
      </div>

      {/* Collapse toggle */}
      {hasChildren && <div style={{
      display: 'flex',
      justifyContent: 'center'
    }} data-id="element-170">
          <button onClick={e => {
        e.stopPropagation();
        onToggle();
      }} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: '3px 8px',
        borderRadius: 6,
        background: collapsed ? `${person.color}15` : '#F8FAFF',
        border: `1px solid ${collapsed ? person.color + '40' : '#E4EAF6'}`,
        color: collapsed ? person.color : '#A0AECB',
        fontSize: 10,
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.15s'
      }} data-id="element-171">
            {collapsed ? <ChevronDownIcon size={11} data-id="element-172" /> : <ChevronUpIcon size={11} data-id="element-173" />}
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>}
    </div>;
}
// ─── Connector Lines ──────────────────────────────────────────────────────────
function OrgNode({
  person,
  depth,
  searchTerm,
  selectedId,
  onSelect
}: {
  person: Person;
  depth: number;
  searchTerm: string;
  selectedId: string | null;
  onSelect: (p: Person | null) => void;
}) {
  const [collapsed, setCollapsed] = useState(depth >= 2);
  const hasChildren = person.children.length > 0;
  const isMatch = searchTerm.length > 0 && (person.name.toLowerCase().includes(searchTerm.toLowerCase()) || person.title.toLowerCase().includes(searchTerm.toLowerCase()) || person.dept.toLowerCase().includes(searchTerm.toLowerCase()));
  const isSelected = selectedId === person.id;
  const showChildren = hasChildren && !collapsed;
  const LINE_COLOR = '#D0DAF0';
  const LINE_WIDTH = 2;
  const V_GAP = 20; // vertical gap between node and horizontal bar
  const H_PAD = 20; // horizontal padding around each child
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  }} data-id="element-174">
      <NodeCard person={person} isMatch={isMatch} isSelected={isSelected} hasChildren={hasChildren} collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} onSelect={onSelect} data-id="element-175" />

      {showChildren && <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }} data-id="element-176">
          {/* Vertical stem from card down */}
          <div style={{
        width: LINE_WIDTH,
        height: V_GAP,
        background: LINE_COLOR,
        flexShrink: 0
      }} data-id="element-177" />

          {person.children.length === 1 ?
      // Single child — straight line, no bracket
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }} data-id="element-178">
              <OrgNode person={person.children[0]} depth={depth + 1} searchTerm={searchTerm} selectedId={selectedId} onSelect={onSelect} data-id="element-179" />
            </div> :
      // Multiple children — bracket connector
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
      }} data-id="element-180">
              {/* Children row with bracket lines */}
              <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          position: 'relative'
        }} data-id="element-181">
                {person.children.map((child, i) => {
            const isFirst = i === 0;
            const isLast = i === person.children.length - 1;
            const isOnly = person.children.length === 1;
            return <div key={child.id} style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingLeft: H_PAD,
              paddingRight: H_PAD,
              paddingTop: V_GAP,
              // Bracket connector lines using borders
              borderTop: `${LINE_WIDTH}px solid ${LINE_COLOR}`,
              borderLeft: isFirst ? `${LINE_WIDTH}px solid ${LINE_COLOR}` : 'none',
              borderRight: isLast ? `${LINE_WIDTH}px solid ${LINE_COLOR}` : 'none',
              borderTopLeftRadius: isFirst ? 8 : 0,
              borderTopRightRadius: isLast ? 8 : 0,
              // Compensate for double border between siblings
              marginLeft: i > 0 ? -LINE_WIDTH : 0
            }} data-id="element-182">
                      <OrgNode person={child} depth={depth + 1} searchTerm={searchTerm} selectedId={selectedId} onSelect={onSelect} data-id="element-183" />
                    </div>;
          })}
              </div>
            </div>}
        </div>}
    </div>;
}
// ─── Detail Panel ─────────────────────────────────────────────────────────────
function DetailPanel({
  person,
  onClose
}: {
  person: Person;
  onClose: () => void;
}) {
  const deptColor = deptColors[person.dept] || '#1A4FD8';
  return <div style={{
    position: 'fixed',
    right: 24,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 260,
    background: '#FFFFFF',
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(10,22,40,0.18)',
    border: '1.5px solid #E4EAF6',
    zIndex: 100,
    overflow: 'hidden'
  }} data-id="element-184">
      {/* Header */}
      <div style={{
      background: `linear-gradient(135deg, ${person.color} 0%, ${person.color}CC 100%)`,
      padding: '20px 16px 16px',
      position: 'relative'
    }} data-id="element-185">
        <button onClick={onClose} style={{
        position: 'absolute',
        top: 10,
        right: 10,
        background: 'rgba(255,255,255,0.2)',
        border: 'none',
        borderRadius: 6,
        color: 'white',
        cursor: 'pointer',
        padding: '2px 6px',
        fontSize: 14,
        fontWeight: 700
      }} data-id="element-186">
          ✕
        </button>
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8
      }} data-id="element-187">
          <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 900,
          color: 'white',
          border: '2px solid rgba(255,255,255,0.4)'
        }} data-id="element-188">
            {person.avatar}
          </div>
          <div data-id="element-189">
            <div style={{
            fontSize: 15,
            fontWeight: 900,
            color: 'white',
            textAlign: 'center',
            fontStyle: 'italic',
            letterSpacing: '-0.02em'
          }} data-id="element-190">
              {person.name}
            </div>
            <div style={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.75)',
            textAlign: 'center',
            marginTop: 2
          }} data-id="element-191">
              {person.title}
            </div>
          </div>
          <span style={{
          fontSize: 10,
          fontWeight: 800,
          padding: '3px 10px',
          borderRadius: 5,
          background: 'rgba(255,255,255,0.2)',
          color: 'white',
          letterSpacing: '0.06em'
        }} data-id="element-192">
            {person.dept}
          </span>
        </div>
      </div>

      {/* Details */}
      <div style={{
      padding: '16px'
    }} data-id="element-193">
        <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }} data-id="element-194">
          <a href={`mailto:${person.email}`} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none'
        }} data-id="element-195">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: `${deptColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-196">
              <MailIcon size={13} color={deptColor} data-id="element-197" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#1A4FD8',
            fontWeight: 500
          }} data-id="element-198">
              {person.email}
            </span>
          </a>
          <a href={`tel:${person.phone}`} style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none'
        }} data-id="element-199">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: `${deptColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-200">
              <PhoneIcon size={13} color={deptColor} data-id="element-201" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#1A4FD8',
            fontWeight: 500
          }} data-id="element-202">
              {person.phone}
            </span>
          </a>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }} data-id="element-203">
            <div style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: `${deptColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }} data-id="element-204">
              <MapPinIcon size={13} color={deptColor} data-id="element-205" />
            </div>
            <span style={{
            fontSize: 12,
            color: '#5A6A8A',
            fontWeight: 500
          }} data-id="element-206">
              {person.location}
            </span>
          </div>
        </div>

        {person.children.length > 0 && <div style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: '1px solid #E4EAF6'
      }} data-id="element-207">
            <div style={{
          fontSize: 10,
          fontWeight: 800,
          color: '#A0AECB',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          marginBottom: 8
        }} data-id="element-208">
              Direct Reports ({person.children.length})
            </div>
            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-209">
              {person.children.map(child => <div key={child.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }} data-id="element-210">
                  <div style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              background: child.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 9,
              fontWeight: 800,
              color: 'white',
              flexShrink: 0
            }} data-id="element-211">
                    {child.avatar}
                  </div>
                  <div data-id="element-212">
                    <div style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#0D1B3E'
              }} data-id="element-213">
                      {child.name}
                    </div>
                    <div style={{
                fontSize: 10,
                color: '#A0AECB'
              }} data-id="element-214">
                      {child.title}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}
// ─── Grid View ────────────────────────────────────────────────────────────────
function GridView({
  people,
  searchTerm
}: {
  people: Person[];
  searchTerm: string;
}) {
  const filtered = searchTerm ? people.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.title.toLowerCase().includes(searchTerm.toLowerCase()) || p.dept.toLowerCase().includes(searchTerm.toLowerCase())) : people;
  return <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16
  }} data-id="element-215">
      {filtered.map(person => {
      const deptColor = deptColors[person.dept] || '#1A4FD8';
      return <div key={person.id} style={{
        background: '#FFFFFF',
        borderRadius: 14,
        padding: '20px 16px',
        border: '1.5px solid #E4EAF6',
        boxShadow: '0 2px 8px rgba(10,22,40,0.06)',
        borderTop: `4px solid ${person.color}`,
        transition: 'all 0.15s'
      }} onMouseEnter={e => {
        ;
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 16px rgba(10,22,40,0.12)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
      }} onMouseLeave={e => {
        ;
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(10,22,40,0.06)';
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
      }} data-id="element-216">
            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12
        }} data-id="element-217">
              <div style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${person.color}, ${person.color}99)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 900,
            color: 'white',
            transform: 'skewX(-3deg)',
            flexShrink: 0
          }} data-id="element-218">
                {person.avatar}
              </div>
              <div data-id="element-219">
                <div style={{
              fontSize: 13,
              fontWeight: 900,
              color: '#0D1B3E',
              fontStyle: 'italic',
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }} data-id="element-220">
                  {person.name}
                </div>
                <div style={{
              fontSize: 11,
              color: '#5A6A8A',
              marginTop: 1
            }} data-id="element-221">
                  {person.title}
                </div>
              </div>
            </div>
            <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }} data-id="element-222">
              <span style={{
            fontSize: 10,
            fontWeight: 800,
            padding: '2px 7px',
            borderRadius: 4,
            background: `${deptColor}15`,
            color: deptColor,
            display: 'inline-block',
            width: 'fit-content'
          }} data-id="element-223">
                {person.dept}
              </span>
              <div style={{
            fontSize: 11,
            color: '#A0AECB',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }} data-id="element-224">
                <MapPinIcon size={10} data-id="element-225" /> {person.location}
              </div>
            </div>
          </div>;
    })}
    </div>;
}
// ─── Main Directory Page ──────────────────────────────────────────────────────
export function Directory() {
  const [zoom, setZoom] = useState(0.85);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'tree' | 'grid'>('tree');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const deptCounts = allPeople.reduce<Record<string, number>>((acc, p) => {
    acc[p.dept] = (acc[p.dept] || 0) + 1;
    return acc;
  }, {});
  return <div style={{
    maxWidth: '100%',
    margin: '0 auto'
  }} data-id="element-226">
      {/* Header */}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
      flexWrap: 'wrap',
      gap: 12
    }} data-id="element-227">
        <div data-id="element-228">
          <h2 style={{
          fontSize: 26,
          fontWeight: 900,
          color: '#0D1B3E',
          margin: '0 0 3px',
          fontStyle: 'italic',
          letterSpacing: '-0.04em'
        }} data-id="element-229">
            Company Directory
          </h2>
          <p style={{
          fontSize: 13,
          color: '#5A6A8A',
          margin: 0
        }} data-id="element-230">
            {allPeople.length} team members · {Object.keys(deptCounts).length}{' '}
            departments
          </p>
        </div>

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }} data-id="element-231">
          {/* Search */}
          <div style={{
          position: 'relative'
        }} data-id="element-232">
            <SearchIcon size={14} style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#A0AECB'
          }} data-id="element-233" />
            <input type="text" placeholder="Search name, title, dept..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            paddingLeft: 30,
            paddingRight: 12,
            paddingTop: 8,
            paddingBottom: 8,
            fontSize: 13,
            borderRadius: 8,
            border: '1.5px solid #E4EAF6',
            background: '#F8FAFF',
            color: '#0D1B3E',
            outline: 'none',
            width: 220
          }} onFocus={e => {
            e.target.style.borderColor = '#1A4FD8';
          }} onBlur={e => {
            e.target.style.borderColor = '#E4EAF6';
          }} data-id="element-234" />
          </div>

          {/* View Toggle */}
          <div style={{
          display: 'flex',
          background: '#F8FAFF',
          borderRadius: 8,
          border: '1.5px solid #E4EAF6',
          overflow: 'hidden'
        }} data-id="element-235">
            <button onClick={() => setView('tree')} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '7px 12px',
            background: view === 'tree' ? '#1A4FD8' : 'transparent',
            color: view === 'tree' ? 'white' : '#5A6A8A',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            transition: 'all 0.15s'
          }} data-id="element-236">
              <GitBranchIcon size={13} data-id="element-237" /> Tree
            </button>
            <button onClick={() => setView('grid')} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '7px 12px',
            background: view === 'grid' ? '#1A4FD8' : 'transparent',
            color: view === 'grid' ? 'white' : '#5A6A8A',
            border: 'none',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            transition: 'all 0.15s'
          }} data-id="element-238">
              <LayoutGridIcon size={13} data-id="element-239" /> Grid
            </button>
          </div>

          {/* Zoom Controls (tree only) */}
          {view === 'tree' && <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: '#F8FAFF',
          borderRadius: 8,
          border: '1.5px solid #E4EAF6',
          padding: '4px 6px'
        }} data-id="element-240">
              <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '3px 5px',
            borderRadius: 5,
            color: '#5A6A8A',
            display: 'flex',
            alignItems: 'center'
          }} title="Zoom out" data-id="element-241">
                <ZoomOutIcon size={15} data-id="element-242" />
              </button>
              <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#5A6A8A',
            minWidth: 36,
            textAlign: 'center'
          }} data-id="element-243">
                {Math.round(zoom * 100)}%
              </span>
              <button onClick={() => setZoom(z => Math.min(1.4, z + 0.1))} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '3px 5px',
            borderRadius: 5,
            color: '#5A6A8A',
            display: 'flex',
            alignItems: 'center'
          }} title="Zoom in" data-id="element-244">
                <ZoomInIcon size={15} data-id="element-245" />
              </button>
              <button onClick={() => setZoom(0.85)} style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '3px 5px',
            borderRadius: 5,
            color: '#5A6A8A',
            display: 'flex',
            alignItems: 'center'
          }} title="Reset zoom" data-id="element-246">
                <RotateCcwIcon size={13} data-id="element-247" />
              </button>
            </div>}
        </div>
      </div>

      {/* Dept legend */}
      <div style={{
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      marginBottom: 20
    }} data-id="element-248">
        {Object.entries(deptColors).map(([dept, color]) => <span key={dept} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 6,
        background: `${color}12`,
        color: color,
        border: `1px solid ${color}25`
      }} data-id="element-249">
            <span style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: color,
          display: 'inline-block'
        }} data-id="element-250" />
            {dept} ({deptCounts[dept] || 0})
          </span>)}
      </div>

      {view === 'grid' ? <GridView people={allPeople} searchTerm={searchTerm} data-id="element-251" /> /* Tree View */ : <div style={{
      background: '#FFFFFF',
      borderRadius: 16,
      border: '1.5px solid #E4EAF6',
      boxShadow: '0 2px 8px rgba(10,22,40,0.06)',
      overflow: 'auto',
      padding: '40px 24px',
      minHeight: 400
    }} data-id="element-252">
          {/* Subtle grid background */}
          <div style={{
        position: 'relative',
        backgroundImage: 'radial-gradient(circle, #D0DAF0 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        borderRadius: 12,
        padding: '32px 16px'
      }} data-id="element-253">
            <div style={{
          transform: `scale(${zoom})`,
          transformOrigin: 'top center',
          transition: 'transform 0.2s ease',
          display: 'flex',
          justifyContent: 'center'
        }} data-id="element-254">
              <OrgNode person={orgTree} depth={0} searchTerm={searchTerm} selectedId={selectedPerson?.id || null} onSelect={setSelectedPerson} data-id="element-255" />
            </div>
          </div>

          {/* Hint */}
          <div style={{
        textAlign: 'center',
        marginTop: 16,
        fontSize: 11,
        color: '#A0AECB'
      }} data-id="element-256">
            Click any card to view details · Use Show/Hide to collapse branches
            · Scroll to explore
          </div>
        </div>}

      {/* Detail Panel */}
      {selectedPerson && <DetailPanel person={selectedPerson} onClose={() => setSelectedPerson(null)} data-id="element-257" />}
    </div>;
}